import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DynamicTable from "../../components/DynamicTable/DynamicTable";
import { useTheme } from "@emotion/react";
import SearchInput from "../../components/VersatileComponents/SearchInput";
import { useParams } from "react-router-dom";
import getHumanReadableDate from "../../utils/humanReadableDate";
import fetchFirestoreDataWithFilterCustomers from "../../api/utils/fetchFirestoreDataWithFilterCustomers";
import { Helmet } from "react-helmet-async";
import ShowBudget from "../../components/Budget/ShowBudget";
import useDocumentById from "../../hooks/useDocumentById";
import getRequiredUserData from "../../utils/getBranchInfo";
import { useAuth } from "../../contexts/AuthContext";
import useUserClaims from "../../hooks/useUserClaims";
import { ExportToExcel } from "../../utils/ExportCustomerToExcel";
import AddCustomerForm from "../../components/User/Customer/AddCustomerForm";
import MembershipForm from "../../components/User/Customer/membershipForm";
import SearchCustomers from "../../api/utils/searchCustomers";
import updateCustomer from "../../api/customer/update";

const columns = [
  { key: "name", title: "Name" },
  { key: "phone", title: "Phone" },
  { key: "blockHouse", title: "Block House" },
  { key: "day", title: "CreatedDate" },
  { key: "lastseen", title: "Lastseen" },
  { key: "Asbeza", title: "Asbeza" },
  { key: "Card", title: "Card" },
  { key: "Water", title: "Water" },
  { key: "Wifi", title: "Wifi" },
  { key: "member", title: "Member", clickable: true },
  { key: "blockStatus", title: "Block Status", clickable: true },
];

const containerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const flexItemStyle = {
  flex: 9,
};

const flexItemStyles = {
  flex: 1,
};

const Customer = () => {
  const params = useParams();
  const theme = useTheme();
  const { user } = useAuth();
  const userClaim = useUserClaims(user);
  const [regularData, setRegularData] = useState([]);
  const [memberData, setMemberData] = useState([]);
  const [blockedData, setBlockedData] = useState([]);
  const [regularLastDoc, setRegularLastDoc] = useState(null);
  const [memberLastDoc, setMemberLastDoc] = useState(null);
  const [blockedLastDoc, setBlockedLastDoc] = useState(null);
  const [searchedData, setSearchedData] = useState([]);
  const [searched, setSearched] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isMembershipDialogOpen, setIsMembershipDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [viewType, setViewType] = useState("regular");
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false); // New state for block confirmation dialog
  const [customerToBlock, setCustomerToBlock] = useState(null); // Track customer to block/unblock
  const userData = getRequiredUserData();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentDate = new Date().toISOString();

  const loadInitialData = async (type) => {
    try {
      let isMember = type === "members";
      let isBlocked = type === "blocked";
      const setData = isBlocked
        ? setBlockedData
        : isMember
        ? setMemberData
        : setRegularData;
      const existingData = isBlocked
        ? blockedData
        : isMember
        ? memberData
        : regularData;

      await fetchFirestoreDataWithFilterCustomers(
        "customer",
        null,
        8,
        existingData,
        setData,
        "branchId",
        params.id,
        { isMember, isBlocked, currentDate }
      );
    } catch (error) {
      console.error(`Error fetching ${type} data:`, error);
    }
  };

  useEffect(() => {
    loadInitialData("regular");
    loadInitialData("members");
    loadInitialData("blocked");
  }, []);

  useEffect(() => {
    if (regularData.length > 0) {
      setRegularLastDoc(regularData[regularData.length - 1]);
    }
    if (memberData.length > 0) {
      setMemberLastDoc(memberData[memberData.length - 1]);
    }
    if (blockedData.length > 0) {
      setBlockedLastDoc(blockedData[blockedData.length - 1]);
    }
  }, [regularData, memberData, blockedData]);

  const handleSearch = async (searchText) => {
    if (searchText.trim() === "") {
      setSearchedData([]);
      setSearched(false);
    } else {
      setSearched(true);
      console.log("Searching for:", searchText);
      SearchCustomers(
        "customer",
        null,
        1000,
        searchedData,
        setSearchedData,
        "branchId",
        params.id,
        "blockHouse",
        searchText.toUpperCase(),
        { viewType, currentDate }
      );
    }
  };

  const handleCancel = () => {
    setSearchedData([]);
    setSearched(false);
  };

  const loadMoreData = useCallback(async () => {
    try {
      const isMember = viewType === "members";
      const isBlocked = viewType === "blocked";
      const lastDoc = isBlocked
        ? blockedLastDoc
        : isMember
        ? memberLastDoc
        : regularLastDoc;
      const setData = isBlocked
        ? setBlockedData
        : isMember
        ? setMemberData
        : setRegularData;
      const existingData = isBlocked
        ? blockedData
        : isMember
        ? memberData
        : regularData;
      if (lastDoc) {
        await fetchFirestoreDataWithFilterCustomers(
          "customer",
          lastDoc,
          5,
          existingData,
          setData,
          "branchId",
          params.id,
          { isMember, isBlocked, currentDate }
        );
      }
    } catch (error) {
      console.error("Error loading more data:", error);
    }
  }, [
    memberLastDoc,
    regularLastDoc,
    blockedLastDoc,
    viewType,
    memberData,
    regularData,
    blockedData,
  ]);

  useEffect(() => {
    const handleDynamicTableScroll = (event) => {
      const scrollPosition = event.detail.scrollPosition;
    };

    window.addEventListener("dynamicTableScroll", handleDynamicTableScroll);

    return () => {
      window.removeEventListener(
        "dynamicTableScroll",
        handleDynamicTableScroll
      );
    };
  }, []);

  const handleOpenBlockDialog = (customer) => {
    if (!userClaim.admin) return;
    setCustomerToBlock(customer);
    setIsBlockDialogOpen(true);
  };

  const handleCloseBlockDialog = () => {
    setIsBlockDialogOpen(false);
    setCustomerToBlock(null);
  };

  const handleBlockCustomer = async () => {
    if (!userClaim.admin || !customerToBlock) return;
    try {
      await updateCustomer(user, {
        blockHouse: customerToBlock.blockHouse,
        isBlocked: !customerToBlock.isBlocked,
      });
    } catch (error) {
      console.error("Error updating block status:", error);
    }
    handleCloseBlockDialog();
  };

  function processArrayOfObjects(data) {
    const currentDateObj = new Date();

    return data?.map((item) => {
      const createdDate = new Date(item.createdDate);
      const dayDifference = Math.floor(
        (currentDateObj - createdDate) / (1000 * 60 * 60 * 24)
      );
      item.lastseen = dayDifference;
      item.day = getHumanReadableDate(item.createdDate);
      const membershipExpiry = item.membershipExpiry
        ? new Date(item.membershipExpiry)
        : null;
      item.member =
        membershipExpiry && membershipExpiry > currentDateObj ? "Yes" : "No";
      item.blockStatus = item.isBlocked ? "Blocked" : "Unblocked";
      return item;
    });
  }

  const processedRegularData = processArrayOfObjects(regularData).filter(
    (item) => !item.isBlocked
  );
  const processedMemberData = processArrayOfObjects(memberData).filter(
    (item) => !item.isBlocked
  );
  const processedBlockedData = processArrayOfObjects(blockedData).filter(
    (item) => item.isBlocked
  );
  const processedSearchData = processArrayOfObjects(searchedData);

  const filteredSearchData = processedSearchData.filter((item) => {
    if (viewType === "members") return item.member === "Yes" && !item.isBlocked;
    if (viewType === "blocked") return item.isBlocked;
    return item.member === "No" && !item.isBlocked;
  });

  const tableData =
    searchedData.length > 0
      ? filteredSearchData
      : viewType === "members"
      ? processedMemberData
      : viewType === "blocked"
      ? processedBlockedData
      : processedRegularData;

  const { documentData: documentData2 } = useDocumentById(
    "branches",
    params.id
  );

  const handleOpenAddDialog = () => {
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    loadInitialData("regular");
    loadInitialData("members");
    loadInitialData("blocked");
  };

  const handleRowClick = (row, columnKey) => {
    if (columnKey === "member") {
      setSelectedCustomer(row);
      setIsMembershipDialogOpen(true);
    } else if (columnKey === "blockStatus" && userClaim.admin) {
      handleOpenBlockDialog(row);
    }
  };

  const handleCloseMembershipDialog = () => {
    setIsMembershipDialogOpen(false);
    setSelectedCustomer(null);
    loadInitialData("regular");
    loadInitialData("members");
    loadInitialData("blocked");
  };

  const handleViewTypeChange = (event, newViewType) => {
    if (newViewType !== null) {
      setViewType(newViewType);
      setSearchedData([]);
      setSearched(false);
    }
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Customers</title>
        <meta name="description" content="List of Customers" />
      </Helmet>
      <Box
        m="1.5rem 2.5rem"
        sx={{
          backgroundColor: theme.palette.background.default,
          height: "100%",
          position: "relative",
        }}
      >
        <Grid container spacing={2}>
          <Grid
            item
            xs={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
            }}
          >
            <Box flex="1">
              <Typography
                variant="h2"
                color={theme.palette.secondary[100]}
                fontWeight="bold"
                sx={{ mb: "5px" }}
              >
                Customer
              </Typography>
              <Typography variant="h5" color={theme.palette.secondary[300]}>
                Entire list of Customers
              </Typography>
            </Box>
          </Grid>

          <Grid
            item
            xs={4}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <SearchInput onSearch={handleSearch} onCancel={handleCancel} />
          </Grid>

          <Grid item xs={4}>
            <ShowBudget
              label={"Total customer"}
              value={
                documentData2
                  ? documentData2.customerNumber
                    ? documentData2.customerNumber
                    : "Not Available"
                  : "Not Available"
              }
              marginTop={10}
            />
          </Grid>
        </Grid>

        <ToggleButtonGroup
          value={viewType}
          exclusive
          onChange={handleViewTypeChange}
          aria-label="customer view type"
          style={{
            marginBottom: "20px",
            marginTop: "20px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <ToggleButton value="regular" aria-label="regular customer">
            Regular Customers
          </ToggleButton>
          <ToggleButton value="members" aria-label="members">
            Members
          </ToggleButton>
          <ToggleButton value="blocked" aria-label="blocked">
            Blocked Customers
          </ToggleButton>
        </ToggleButtonGroup>

        <div style={containerStyle}>
          <div style={flexItemStyle}></div>
          <div style={flexItemStyles}>
            {userClaim.superAdmin || userClaim.subAdmin ? (
              <ExportToExcel
                file={"customer"}
                branchId={userData.requiredId}
                id={""}
                endpoint={"branchCustomer"}
                clear={false}
                name={`BranchCustomerTable-Branch ${userData.branchName}`}
              />
            ) : null}
          </div>
        </div>

        <DynamicTable
          data={tableData}
          columns={columns}
          loadMoreData={loadMoreData}
          onRowClick={handleRowClick}
        />

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            borderRadius: 28,
            padding: isMobile ? "8px 16px" : "12px 24px",
            fontSize: isMobile ? "0.875rem" : "1rem",
            fontWeight: "bold",
            backgroundColor: theme.palette.primary.dark,
            boxShadow: 3,
            "&:hover": {
              boxShadow: 6,
            },
            zIndex: 1000,
            minWidth: "auto",
            whiteSpace: "nowrap",
          }}
        >
          {isMobile ? "Add" : "Add Customer"}
        </Button>

        <AddCustomerForm
          open={isAddDialogOpen}
          onClose={handleCloseAddDialog}
          branchId={params.id}
        />

        <MembershipForm
          open={isMembershipDialogOpen}
          onClose={handleCloseMembershipDialog}
          customer={selectedCustomer}
          branchId={params.id}
        />

        <Dialog
          open={isBlockDialogOpen}
          onClose={handleCloseBlockDialog}
          aria-labelledby="block-confirm-dialog-title"
          aria-describedby="block-confirm-dialog-description"
        >
          <DialogTitle id="block-confirm-dialog-title">
            Confirm Block/Unblock
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="block-confirm-dialog-description">
              Are you sure you want to{" "}
              {customerToBlock?.isBlocked ? "unblock" : "block"}{" "}
              {customerToBlock?.name} at {customerToBlock?.blockHouse}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBlockDialog} color="secondary">
              No
            </Button>
            <Button onClick={handleBlockCustomer} color="secondary" autoFocus>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default Customer;
